import React, { useMemo, useState } from 'react';
import { Button } from 'reactstrap';
import moment from 'moment';

//Components
import TabelaListagem from '../../../../../components/TabelaListagem/TabelaListagem';
import ModalCustomized from '../../../../../components/Modal/Modal';

//Helpers
import { showMessage, showQuestion } from '../../../../../helpers/message';
import { formatarValor } from '../../../../../helpers/reajusteInfracao';
import { actionsTable } from '../../../../../constants/MaterialTable';

//API
import infracoes_api from '../../../../../api/infracoes_api';

//Context
import { useAuth } from '../../../../../context/auth';

const formatarData = (data) => data ? moment(data).format("DD/MM/YYYY") : "-";

const formatarDataHora = (data) => data ? moment(data).format("DD/MM/YYYY HH:mm") : "-";

const formatarFator = (fator) => Number(fator ?? 0).toFixed(4).replace(".", ",");

// Cancelamento só é aceito pelo backend para reajuste agendado cuja vigência ainda não começou
const podeCancelar = (reajuste) =>
    reajuste.status === "Agendado" && moment(reajuste.inicioVigencia).isAfter(moment(), "day");

export default function HistoricoReajustesInfracoes({ reajustes, podeRecalcular, onAlterado }) {
    const { setLoading } = useAuth();

    //Reajuste cujo detalhe (itens) está aberto e seus itens
    const [reajusteDetalhe, setReajusteDetalhe] = useState(null);
    const [itensDetalhe, setItensDetalhe] = useState([]);

    //Evita cancelamentos simultâneos
    const [cancelando, setCancelando] = useState(false);

    const items = useMemo(() => reajustes.map(reajuste => ({
        ...reajuste,
        inicioVigenciaFormatada: formatarData(reajuste.inicioVigencia),
        fimVigenciaFormatada: formatarData(reajuste.fimVigencia),
        fatorFormatado: formatarFator(reajuste.fatorCorrecao),
        dataCadastroFormatada: formatarDataHora(reajuste.dataCadastro),
        dataAplicacaoFormatada: formatarDataHora(reajuste.dataAplicacao),
        observacaoFormatada: reajuste.observacao ?? "-"
    })), [reajustes]);

    const abrirDetalhe = async (reajuste) => {
        setLoading(true);
        try {
            const { data } = await infracoes_api.GetReajusteItens(reajuste.id);
            setItensDetalhe((data ?? []).map(item => ({
                ...item,
                valorAnteriorFormatado: formatarValor(item.valorAnterior),
                valorNovoFormatado: formatarValor(item.valorNovo),
                reincidenciaAnteriorFormatada: item.valorReincidenciaAnterior === null ? "-" : formatarValor(item.valorReincidenciaAnterior),
                reincidenciaNovaFormatada: item.valorReincidenciaNovo === null ? "-" : formatarValor(item.valorReincidenciaNovo),
                editadoFormatado: item.editadoManualmente ? "Sim" : "Não"
            })));
            setReajusteDetalhe(reajuste);
        } catch (err) {
            showMessage("Erro", err?.message ?? "Erro ao buscar os itens do reajuste", "error", null);
        } finally {
            setLoading(false);
        }
    };

    const fecharDetalhe = () => {
        setReajusteDetalhe(null);
        setItensDetalhe([]);
    };

    const cancelarAgendamento = async (reajuste) => {
        setCancelando(true);
        setLoading(true);
        try {
            await infracoes_api.CancelarReajuste(reajuste.id);
            showMessage("Aviso", "Agendamento cancelado com sucesso.", "success", () => onAlterado());
        } catch (err) {
            showMessage("Erro", err?.message ?? "Erro ao cancelar o agendamento", "error", null);
        } finally {
            setLoading(false);
            setCancelando(false);
        }
    };

    const confirmarCancelamento = (reajuste) => {
        if (cancelando) return;

        showQuestion(
            "Cancelar agendamento",
            `Deseja cancelar o reajuste "${reajuste.atoAdministrativo}" com vigência a partir de ${formatarData(reajuste.inicioVigencia)}?`,
            "question",
            (confirmado) => { if (confirmado) cancelarAgendamento(reajuste); }
        );
    };

    const actions = [
        actionsTable.visualizar(abrirDetalhe, "Visualizar itens do reajuste"),
        (reajuste) => podeRecalcular && podeCancelar(reajuste)
            ? actionsTable.remover(() => confirmarCancelamento(reajuste), "Cancelar agendamento")
            : null
    ];

    const headers = [
        { value: "Ato Administrativo", objectValue: "atoAdministrativo", align: "left" },
        { value: "Início da Vigência", objectValue: "inicioVigenciaFormatada", align: "left" },
        { value: "Fim da Vigência", objectValue: "fimVigenciaFormatada", align: "left" },
        { value: "Fator de Correção", objectValue: "fatorFormatado", align: "left" },
        { value: "Observação", objectValue: "observacaoFormatada", align: "left" },
        { value: "Data do Cadastro", objectValue: "dataCadastroFormatada", align: "left" },
        { value: "Usuário", objectValue: "usuario", align: "left" },
        { value: "Status", objectValue: "status", align: "left" },
        { value: "Data da Aplicação", objectValue: "dataAplicacaoFormatada", align: "left" }
    ];

    const headersDetalhe = [
        { value: "Infração", objectValue: "identificacao", align: "left" },
        { value: "Valor Anterior (UFM)", objectValue: "valorAnteriorFormatado", align: "left" },
        { value: "Novo Valor (UFM)", objectValue: "valorNovoFormatado", align: "left" },
        { value: "Reincidência Anterior (UFM)", objectValue: "reincidenciaAnteriorFormatada", align: "left" },
        { value: "Nova Reincidência (UFM)", objectValue: "reincidenciaNovaFormatada", align: "left" },
        { value: "Editado Manualmente", objectValue: "editadoFormatado", align: "left" }
    ];

    if (items.length === 0)
        return null;

    return (
        <>
            <TabelaListagem
                actions={actions}
                title={"Histórico de Reajustes"}
                headers={headers}
                items={items}
            />

            {reajusteDetalhe && (
                <ModalCustomized
                    size={'xl'}
                    fullscreen={"lg"}
                    toggle={fecharDetalhe}
                    widthPadrao={false}
                    isOpen={!!reajusteDetalhe}
                    btnClose={fecharDetalhe}
                    title={`Itens do reajuste - ${reajusteDetalhe.atoAdministrativo}`}
                >
                    <TabelaListagem
                        title={`Vigência de ${formatarData(reajusteDetalhe.inicioVigencia)} a ${formatarData(reajusteDetalhe.fimVigencia)}`}
                        headers={headersDetalhe}
                        items={itensDetalhe}
                    />
                    <div className="d-flex justify-content-end">
                        <Button color="secondary" onClick={fecharDetalhe}>Fechar</Button>
                    </div>
                </ModalCustomized>
            )}
        </>
    );
}
